import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { OpeningLayoutBounds, PerimeterLayoutBounds } from "./framingLayoutTypes";
import type { BoundaryVertex, FramingBoundaryWithMetadata } from "./framingMetaDataTypes";
import type { FramingProfileDescriptor } from "./framingProfileDescriptorTypes";
import type { StudEndHit, StudEndTrimContext } from "./wallFramingStudEndTrimTypes";
import type { WallFramingType } from "./wallFramingTypes";

export interface OpeningFramingExtents {
  readonly openingIndex: number;
  /** Outside face of the left king stud (minimum perpendicular). */
  readonly startPerp: number;
  /** Outside face of the right king stud (maximum perpendicular). */
  readonly endPerp: number;
  readonly headerTopParallel: number;
  readonly sillBottomParallel: number;
}

export interface WallOpeningFramingMemberOptions {
  readonly includeKings?: boolean;
  readonly includeJacks?: boolean;
  readonly includeHeaders?: boolean;
  readonly includeHeaderSills?: boolean;
  readonly includeSills?: boolean;
  /** Generates top cripples (above the header) for the opening when enabled. */
  readonly includeCripples?: boolean;
  readonly jackBottomParallel?: number;
  readonly jackTopParallel?: number;
  /**
   * When set (shared king-stud column), jacks and sills use this combined rough-opening perp
   * range; headers still span jack outside faces on the same bounds.
   */
  readonly assemblyLayoutBounds?: OpeningLayoutBounds;
}

export enum WallOpeningFramingMode {
  WINDOW = "WINDOW",
  DOOR = "DOOR",
}

export interface ValidatedWallOpening {
  readonly openingIndex: number;
  readonly bounds: OpeningLayoutBounds;
  readonly mode: WallOpeningFramingMode;
}

export interface WallOpeningValidationResult {
  readonly validOpenings: ValidatedWallOpening[];
  readonly errors: string[];
  readonly doorBottomPlateGaps: DoorBottomPlateGap[];
}

export interface WallOpeningValidationOptions {
  readonly perimeterPolyline?: PolyLine2;
  readonly openingPolylines?: readonly PolyLine2[];
}

export interface DoorBottomPlateGap {
  readonly startPerp: number;
  readonly endPerp: number;
}

export interface WallOpeningFramingContext {
  readonly planeOrigin: Vec2;
  readonly perpDirection: Vec2;
  readonly framingDirection: Vec2;
  readonly perimeterBounds: PerimeterLayoutBounds;
  readonly studBottomParallel: number;
  readonly studTopParallel: number;
  readonly spacing: number;
  /** Spacing grid direction from layout start; used to place stud rails on centerline targets. */
  readonly spacingSign: 1 | -1;
  /** Half stud run thickness (profile.height / 2). */
  readonly studHalfThickness: number;
  readonly sortedVertices: BoundaryVertex[];
  readonly perimeter?: FramingBoundaryWithMetadata;
  readonly studEndTrimCtx?: StudEndTrimContext;
}

export interface GeneratedWallOpeningMember {
  readonly polyline: PolyLine2;
  readonly profileDescriptor: FramingProfileDescriptor;
  readonly framingType: WallFramingType;
  readonly depthStackIndex: number;
  readonly startHit?: StudEndHit;
  readonly endHit?: StudEndHit;
  /**
   * Indices (into the generator's `openings` array) of the opening(s) this member belongs to. Most
   * members reference a single opening; shared members (band king studs, cripples between two
   * stacked openings) reference more than one. Mapped to doc opening ids by the generator.
   */
  readonly openingIndices?: readonly number[];
  /**
   * Shared id linking the depth-stacked plies of a single physical header. Set only on HEADER
   * members so downstream consolidation can regroup the per-ply members into one header holding
   * all plies as rails (matching the legacy pipeline's shape).
   */
  readonly headerGroupId?: string;
}
